import React, { useEffect, useState } from 'react';
import { Button, DialogContent, Grid, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { useTranslation } from 'react-i18next';
import { RefundablePaymentData, RefundOwnerType, VoucherService } from '../api';
import { ColorButton } from '../common/ui/ConfimationDialog';
import { UseAuthPermissions } from '../session/UseAuthPermissions';
import { RefundDialog } from './RefundDialog';
import { useRefundAmountFormatter } from './refundFormat';
import { useRefundOverview } from './useRefundOverview';

type RefundOfferDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  ownerType: RefundOwnerType;
  ownerId: string;
  title: string;
  customerName: string;
  ownerLabel: string;
  voucherBalance?: number;
};

export type RefundOfferTarget = Omit<RefundOfferDialogProps, 'isOpen' | 'onClose'>;

export const RefundOfferDialog = ({
  isOpen,
  onClose,
  ownerType,
  ownerId,
  title,
  customerName,
  ownerLabel,
  voucherBalance,
}: RefundOfferDialogProps) => {
  const { t } = useTranslation();
  const { readOnly } = UseAuthPermissions();
  const formatAmount = useRefundAmountFormatter();
  const { overview, mutate } = useRefundOverview(ownerType, ownerId);
  const [refundItem, setRefundItem] = useState<RefundablePaymentData | undefined>(undefined);
  const [refreshedVoucherBalance, setRefreshedVoucherBalance] = useState<number | undefined>(undefined);

  const payments = overview?.payments ?? [];
  const hasNoPayments = !!overview && payments.length === 0;

  useEffect(() => {
    if (hasNoPayments) {
      onClose();
    }
  }, [hasNoPayments]);

  if (!overview || hasNoPayments) {
    return null;
  }

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="xs">
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Grid container flexDirection="column" flexWrap="nowrap" gap={2}>
            <Typography>{t('refunds.offer.body', { name: customerName })}</Typography>
            {payments.map((item) => {
              const disabledReason = readOnly
                ? t('noPermissions')
                : item.disabled_reason
                ? t(`refunds.disabled.${item.disabled_reason}`)
                : '';
              return (
                <Grid
                  key={item.charge_id ?? `${item.entity_type}-${item.entity_id}`}
                  container
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="nowrap"
                  gap={1.5}
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="divider"
                  p={1.5}>
                  <Grid container flexDirection="column" flexWrap="nowrap" flex={1} minWidth={0}>
                    <Typography fontWeight={600}>{item.label ?? t(`refunds.entity.${item.entity_type}`)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {disabledReason ||
                        t('refunds.offer.available', { amount: formatAmount(item.refundable_amount ?? 0) })}
                    </Typography>
                  </Grid>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={!!disabledReason}
                    onClick={() => setRefundItem(item)}>
                    {t('refunds.offer.refund')}
                  </Button>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <ColorButton onClick={onClose}>{t('refunds.offer.notNow')}</ColorButton>
        </DialogActions>
      </Dialog>
      <RefundDialog
        isOpen={!!refundItem}
        onClose={() => setRefundItem(undefined)}
        item={refundItem}
        ownerLabel={ownerLabel}
        voucherBalance={refreshedVoucherBalance ?? voucherBalance}
        onRefunded={async () => {
          void mutate();
          if (ownerType === RefundOwnerType.VOUCHER) {
            const refreshedVoucher = await VoucherService.getVoucherById(ownerId).catch(() => undefined);
            if (refreshedVoucher) {
              setRefreshedVoucherBalance(refreshedVoucher.balance);
            }
          }
        }}
      />
    </>
  );
};
